import { useEffect, useMemo, useRef, useState } from "react";
import { templates } from "@shared/templates";
import { PinInput, PIN_WIDTH, TemplateName } from "@shared/templates/types";

type Props = {
  template: TemplateName;
  data: PinInput;
};

export function PinPreview({ template, data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / PIN_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const result = useMemo<{ html?: string; error?: string }>(() => {
    try {
      return { html: templates[template](data) };
    } catch (e) {
      return { error: e instanceof Error ? e.message : String(e) };
    }
  }, [template, data]);

  return (
    <div className="preview-frame" ref={containerRef}>
      {result.html && scale > 0 && (
        <iframe
          srcDoc={result.html}
          title="Pin preview"
          style={{ transform: `scale(${scale})` }}
        />
      )}
      {result.error && (
        <div className="preview-error">
          <strong>Preview unavailable</strong>
          <code>{result.error}</code>
          <p>Fill in the missing field on the left and the preview will render.</p>
        </div>
      )}
    </div>
  );
}
